import { EventBusyOutlined as EventBusyOutlinedIcon } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { LocationEntity } from '@peritasai/repository';
import { getSurgeonSurname, ListCard } from '../../../shared';
import type {
  AssignToCaseCase,
  AssignToCaseSelectionLabels,
} from '../types/assign-to-case';
import {
  cancelSelectionButtonSx,
  cardTitleSx,
  caseCardWrapSx,
  caseListCardStackSx,
  caseListEmptyIconSx,
  caseListEmptyMessageSx,
  caseListEmptyStateSx,
  caseListRowDetailsSx,
  caseListRowWithGapSx,
  caseListScrollSx,
  caseSubtitleSx,
  caseTitleSx,
  locationHeaderRowSx,
  locationTitleSx,
  selectButtonSx,
  selectCaseHeadingSx,
  selectedFlowHeaderTitleStackSx,
  selectedFlowLocationSx,
  selectedFlowTitleSx,
} from './styles';

type CaseListCardProps = {
  cases: AssignToCaseCase[];
  location: LocationEntity;
  hasSelectedLocation: boolean;
  showEmbeddedPageTitle: boolean;
  showAll: boolean;
  showViewAll: boolean;
  showCancel: boolean;
  labels: AssignToCaseSelectionLabels;
  onToggleViewAll: () => void;
  onSelect: (selected: AssignToCaseCase) => void;
  onCancel: () => void;
};

export const CaseListCard = ({
  cases,
  location,
  hasSelectedLocation,
  showEmbeddedPageTitle,
  showAll,
  showViewAll,
  showCancel,
  labels,
  onToggleViewAll,
  onSelect,
  onCancel,
}: CaseListCardProps) => {
  const listHeight = showAll
    ? {
        xs: 'calc(100dvh - 360px)',
        md: 'calc(100vh - 360px)',
        lg: 'calc(100dvh - 360px)',
      }
    : 'auto';

  return (
    <Box sx={caseCardWrapSx}>
      <Box sx={caseListCardStackSx(hasSelectedLocation)}>
        <Typography
          sx={{ ...locationTitleSx, display: { xs: 'block', md: 'none' } }}
        >
          {location.name}
        </Typography>
        <Box
          sx={{ ...locationHeaderRowSx, display: { xs: 'none', md: 'flex' } }}
        >
          {showEmbeddedPageTitle ? (
            <Box sx={selectedFlowHeaderTitleStackSx}>
              <Typography sx={selectedFlowTitleSx}>
                {labels.featureTitle}
              </Typography>
              <Typography sx={selectedFlowLocationSx}>
                {location.name}
              </Typography>
            </Box>
          ) : (
            <Typography sx={locationTitleSx}>{location.name}</Typography>
          )}
          {showCancel && (
            <Button
              variant="outlined"
              size="large"
              onClick={onCancel}
              sx={{
                ...cancelSelectionButtonSx,
                display: { xs: 'none', md: 'inline-flex' },
              }}
            >
              {labels.changeLocationButtonLabel}
            </Button>
          )}
        </Box>
        <ListCard
          sx={
            showAll
              ? {
                  width: { xs: '450px', md: '100%' },
                  maxWidth: { xs: '450px', md: '100%' },
                  height: {
                    xs: 'calc(100dvh - 220px)',
                    md: 'calc(100vh - 187px)',
                  },
                  padding: { xs: '1.5rem 1rem 1rem 1rem', md: '24px' },
                  overflow: 'hidden',
                  gap: { xs: '16px', md: '16px' },
                  alignItems: { xs: 'center', md: 'stretch' },
                }
              : {
                  width: { xs: '450px', md: '100%' },
                  maxWidth: { xs: '450px', md: '100%' },
                  padding: { xs: '40px 32px', md: '24px' },
                  overflow: 'visible',
                  gap: { xs: '40px', md: '16px' },
                  alignItems: { xs: 'center', md: 'stretch' },
                }
          }
          showViewAll={showViewAll}
          expanded={showAll}
          onToggleViewAll={onToggleViewAll}
          viewAllLabel={
            showAll
              ? labels.hideAdditionalCasesLabel
              : labels.viewAllCasesLabel
          }
        >
          <Box sx={selectCaseHeadingSx}>
            <Typography sx={cardTitleSx}>{labels.caseListTitle}</Typography>
          </Box>
          {cases.length === 0 && (
            <Box sx={caseListEmptyStateSx} data-testid="case-list-empty-state">
              <EventBusyOutlinedIcon sx={caseListEmptyIconSx} />
              <Typography sx={caseListEmptyMessageSx}>
                {labels.noCasesMessage}
              </Typography>
              {showCancel && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={onCancel}
                  sx={cancelSelectionButtonSx}
                  data-testid="case-list-empty-change-location"
                >
                  {labels.changeLocationButtonLabel}
                </Button>
              )}
            </Box>
          )}
          {cases.length > 0 && (
            <Box sx={caseListScrollSx(showAll, listHeight)}>
              {cases.map((caseItem) => (
                <Box
                  key={caseItem.id}
                  sx={caseListRowWithGapSx}
                  onClick={() => onSelect(caseItem)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelect(caseItem);
                    }
                  }}
                >
                  <Box sx={caseListRowDetailsSx}>
                    <Typography sx={caseTitleSx}>
                      {caseItem.timeLabel},{' '}
                      {getSurgeonSurname(caseItem.surgeonName)}
                    </Typography>
                    <Typography sx={caseSubtitleSx}>
                      {caseItem.procedureName}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="large"
                    sx={selectButtonSx}
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelect(caseItem);
                    }}
                  >
                    {labels.selectButtonLabel}
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </ListCard>
      </Box>
    </Box>
  );
};
